/**
 * LicensePanel (P270.4) -- admin license status + upload.
 *
 * Route: /admin/license. Shows the current deployment-license state
 * (customer / expiry / state / licensed modules / gate on-off) and an
 * upload form for a renewed `.jwt`. The upload form is also reused by the
 * blocking LicenseGate overlay.
 *
 * Hardcoded English to match the sibling shared/admin exec surfaces.
 */
import { formatDate } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { EscalationLine } from "shared/components/EscalationLine";

import { useMe } from "shared/api/useMe";
import type { LicenseStatus } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";
import {
  useLicenseStatus,
  useSyncLicense,
  useUploadLicense,
} from "shared/licensing/useLicense";

const STATE_TONE: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  grace: "bg-amber-100 text-amber-900 ring-amber-300",
  expired: "bg-rose-100 text-rose-800 ring-rose-300",
  missing: "bg-slate-100 text-slate-700 ring-slate-300",
  invalid: "bg-rose-100 text-rose-800 ring-rose-300",
  revoked: "bg-rose-100 text-rose-800 ring-rose-300",
};

/** Whole days from now until an epoch-seconds expiry, or null. */
function daysUntil(epoch: number | null): number | null {
  if (!epoch) return null;
  return Math.ceil((epoch * 1000 - Date.now()) / 86_400_000);
}

function fmtDate(epoch: number | null): string {
  return epoch ? formatDate(new Date(epoch * 1000), i18n.language) : "—";
}

/**
 * D9 (round 8) -- these were the only Latin runs left on a fully
 * pseudo-localised page, unchanged character for character across three
 * rounds. They are English sentences in a helper, which is where the
 * copy rule cannot see them: it reads literals in JSX children.
 */
function fmtSynced(
  epoch: number | null | undefined,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (!epoch) return t("screens.licensePanel.syncNever");
  const mins = Math.max(0, Math.round((Date.now() / 1000 - epoch) / 60));
  if (mins < 1) return t("screens.licensePanel.syncJustNow");
  if (mins < 60) return t("screens.licensePanel.syncMinutesAgo", { count: mins });
  const hrs = Math.round(mins / 60);
  return hrs < 24
    ? t("screens.licensePanel.syncHoursAgo", { count: hrs })
    : t("screens.licensePanel.syncDaysAgo", { count: Math.round(hrs / 24) });
}

export function LicenseUploadForm({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const upload = useUploadLicense();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const onSubmit = () => {
    const file = inputRef.current?.files?.[0];
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (s) =>
        toast.push({ tone: "success", message: `License installed (${s.state})` }),
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  };

  return (
    <div className="space-y-2" data-testid="license-upload-form">
      <input
        ref={inputRef}
        type="file"
        accept=".jwt,text/plain"
        data-testid="license-file-input"
        onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
        className="block text-xs text-slate-600"
      />
      <button
        type="button"
        data-testid="license-upload-button"
        disabled={!fileName || upload.isPending}
        onClick={onSubmit}
        className={`rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50 ${
          compact ? "" : "mt-1"
        }`}
      >
        {upload.isPending
          ? t("screens.licensePanel.installing")
          : t("screens.licensePanel.uploadLicense")}
      </button>
    </div>
  );
}

function StatusRow({ status }: { status: LicenseStatus }) {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-slate-500">{t("screens.licensePanel.state")}</dt>
      <dd>
        <span
          data-testid="license-state-badge"
          className={`rounded px-2 py-0.5 text-xs font-semibold ring-1 ${
            STATE_TONE[status.state] ?? STATE_TONE.missing
          }`}
        >
          {/* Round 10 F6 -- the state was the column value, the only
              Latin run left on an otherwise localised page. It is a
              closed set of six and the product owns every one of
              them. */}
          {t(`screens.licensePanel.stateWord.${status.state}`, {
            defaultValue: status.state,
          })}
        </span>
        {!status.gate_enabled && (
          <span className="text-xs text-slate-400">
            {/* ...and a separator in the TEXT. `ms-2` spaces it on
                screen and concatenates the moment anybody copies the
                line: "missing(enforcement off -- estate open)". Same
                defect the classification line carried for three
                rounds. */}{" "}
            {t("screens.licensePanel.enforcementOffEstateOpen")}
          </span>
        )}
      </dd>
      <dt className="text-slate-500">{t("screens.licensePanel.customer")}</dt>
      <dd>{status.customer_name || "—"}</dd>
      <dt className="text-slate-500">{t("screens.licensePanel.expires")}</dt>
      <dd>
        {fmtDate(status.expires_at)}
        {/* H13 -- the page showed a date and nothing else. An expiry is
            only useful if you notice it BEFORE it bites, and an
            administrator cannot renew a licence themselves, so the
            warning names the issuer too. Thirty days is the window in
            which a procurement conversation still has time to happen. */}
        {status.state === "active" &&
          daysUntil(status.expires_at) !== null &&
          daysUntil(status.expires_at)! <= 30 && (
            <span data-testid="license-expiring-soon" className="ms-2 text-amber-700">
              {t("screens.licensePanel.expiringSoon", {
                count: daysUntil(status.expires_at) ?? 0,
              })}
            </span>
          )}
        {status.state === "grace" && status.grace_days_remaining != null && (
          <span className="ms-2 text-amber-700">
            {t("screens.licensePanel.grace")} {status.grace_days_remaining}{" "}
            {t("screens.licensePanel.daySLeft")}
          </span>
        )}
      </dd>
      <dt className="text-slate-500">{t("screens.licensePanel.licensedModules")}</dt>
      <dd data-testid="license-modules">
        {status.modules === null
          ? t("screens.licensePanel.allUnscopedLicense")
          : status.modules.length
            ? status.modules.join(", ")
            : t("screens.licensePanel.none")}
      </dd>
      {status.license_id && (
        <>
          <dt className="text-slate-500">{t("screens.licensePanel.licenseId")}</dt>
          <dd data-testid="license-id" className="font-mono text-xs text-slate-600">
            {status.license_id.slice(0, 8)}…
          </dd>
        </>
      )}
      <dt className="text-slate-500">{t("screens.licensePanel.lastSynced")}</dt>
      <dd data-testid="license-last-synced" className="text-slate-600">
        {fmtSynced(status.last_synced_at, t)}
      </dd>
      {status.error_message && (
        <>
          <dt className="text-slate-500">{t("screens.licensePanel.error")}</dt>
          <dd className="text-rose-700">
            {status.error_message}
            {/* E5 -- a refused licence named the fault and nobody to
                tell. An administrator cannot fix a licence; only the
                issuer can. */}
            <EscalationLine
              testid="license-escalate"
              // D4 -- the same sentence is already in the catalogue and
              // was typed again here, so one of the two escalation
              // lines on this page translated and the other did not.
              who={t("escalate.licence")}
              details={{
                what: `MARSAD licence is ${status.state}`,
                context: {
                  State: status.state,
                  "Licence id": status.license_id ?? "",
                  "Deployment id": status.deployment_id ?? "",
                  Expires: status.expires_at ?? "",
                  "Last synced": status.last_synced_at ?? "never",
                  Error: status.error_message,
                },
              }}
            />
          </dd>
        </>
      )}
    </dl>
  );
}

function SyncNowSection() {
  const { t } = useTranslation();
  const sync = useSyncLicense();
  const toast = useToast();
  return (
    <section className="space-y-2 rounded-lg border border-slate-200 bg-white p-5">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("screens.licensePanel.onlineSync")}
      </h2>
      <p className="text-sm text-slate-600">
        {t("screens.licensePanel.refreshARenewedLicenseApplyTheRevocation")}
      </p>
      <button
        type="button"
        data-testid="license-sync-button"
        disabled={sync.isPending}
        onClick={() =>
          sync.mutate(undefined, {
            onSuccess: (s) =>
              toast.push({ tone: "success", message: `Synced (${s.state})` }),
            onError: (e) => toast.push({ tone: "error", message: e.message }),
          })
        }
        className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {sync.isPending
          ? t("screens.licensePanel.syncing")
          : t("screens.licensePanel.syncNow")}
      </button>
    </section>
  );
}

export function LicensePanel() {
  const { t } = useTranslation();
  const me = useMe();
  const status = useLicenseStatus();

  if (!me.data?.is_admin) {
    // D12 -- a single red line of refusal, where every other admin
    // surface says what the page is and who to ask. The shared panel is
    // that answer; a bespoke denial here was just the older way.
    return <AdminOnlyPanel surface={t("screens.licensePanel.deploymentLicense")} />;
  }

  return (
    <div data-testid="license-panel" className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      <header>
        <h1 className="font-display text-2xl text-slate-900">
          {t("screens.licensePanel.deploymentLicense")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {t("screens.licensePanel.theSignedLicenseThatGatesWhichModules")}
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        {status.isLoading && (
          <p className="text-sm text-slate-400">{t("screens.licensePanel.loading")}</p>
        )}
        {status.data && <StatusRow status={status.data} />}
        {/* D12 -- with no licence installed the page listed a state and
            stopped: no expiry, no contact, and nothing about what
            happens next. An administrator cannot issue a licence, so
            what he needs is what the deployment does WITHOUT one and
            who to ask for one. */}
        {status.data && status.data.state === "missing" && (
          <div
            data-testid="license-missing-note"
            className="mt-3 space-y-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
          >
            <p>
              {status.data.gate_enabled
                ? t("screens.licensePanel.missingEnforcing")
                : t("screens.licensePanel.missingOpen")}
            </p>
            <EscalationLine
              testid="license-missing-escalate"
              who={t("escalate.licence")}
              details={{
                what: t("escalate.whatNoLicence"),
                context: {
                  "Enforcement enabled": String(status.data.gate_enabled),
                },
              }}
            />
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.licensePanel.installRenew")}
        </h2>
        <p className="text-sm text-slate-600">
          {t("screens.licensePanel.uploadAJwtLicenseIssuedByThe")}
        </p>
        <LicenseUploadForm />
      </section>

      <SyncNowSection />
    </div>
  );
}
